import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Banknote, Plus, Trash2 } from "lucide-react";

const BUILT_IN = [["Cash", "Always available"], ["Card reader (Azordi Terminal)", "Stripe Terminal"], ["Card — keyed in / on file", "Stripe, saved to the client"], ["Bank transfer", "Record only"], ["Other", "Record only"]];

// Setup → Business → Payment methods: your own tender types (Afterpay, Zip, Square terminal…) for checkout, deposits & reports.
export const PaymentMethodsCard = () => {
  const { business, setBusiness } = useAuth();
  const list = business?.payment_methods || [];
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (next) => {
    setBusy(true);
    try { const { data } = await api.put("/business", { payment_methods: next }); setBusiness(data); toast.success("Payment methods saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const add = () => { const n = name.trim(); if (!n) return; if (list.some((m) => m.name.toLowerCase() === n.toLowerCase())) return toast.error("Already added"); save([...list, { name: n, enabled: true }]); setName(""); };
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-4 mt-5" data-testid="payment-methods-card">
      <div className="flex items-center gap-2 mb-1"><Banknote className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Payment methods</h3></div>
      <p className="text-xs text-muted-foreground -mt-2">Add the ways clients pay you outside Azordi (your own EFTPOS terminal, Afterpay, Zip…). They appear at checkout, quick sale and deposits, and get their own line in Sales reports.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {BUILT_IN.map(([n, sub]) => <div key={n} className="rounded-lg border border-dashed p-2.5 text-sm text-muted-foreground"><div className="font-medium text-foreground">{n}</div><div className="text-[11px]">{sub}</div></div>)}
        {list.map((m) => (
          <div key={m.id || m.name} className="rounded-lg border p-2.5 text-sm flex items-center gap-2" data-testid={`pm-row-${m.id}`}>
            <Switch checked={m.enabled !== false} onCheckedChange={(v) => save(list.map((x) => x.id === m.id ? { ...x, enabled: v } : x))} disabled={busy} data-testid={`pm-toggle-${m.id}`} />
            <span className={`flex-1 font-medium ${m.enabled === false ? "text-muted-foreground line-through" : ""}`}>{m.name}</span>
            <button onClick={() => save(list.filter((x) => x.id !== m.id))} disabled={busy} className="text-muted-foreground hover:text-destructive" data-testid={`pm-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Afterpay, Zip, Square terminal" className="h-10 rounded-lg" data-testid="pm-new-name" />
        <Button variant="outline" onClick={add} disabled={busy || !name.trim()} className="h-10 rounded-lg shrink-0" data-testid="pm-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
    </Card>
  );
};

export const useCustomMethods = () => { const { business } = useAuth(); return (business?.payment_methods || []).filter((m) => m.enabled !== false); };
